import React, { useEffect, useState } from 'react';
import { colors } from '../../../colors';
import Checkbox from '../../../components/Checkbox';
import {
  useEditAreNotificationsEnabledMutation,
  useEditUserSavingsEnabledMutation,
  useGetUserSavingsEnabledQuery,
  useGetHideNearZeroEnabledQuery,
  useUpdateHideNearZeroEnabledMutation,
} from '../../../store/services/api';
import { useSelector } from 'react-redux';
import { selectUserId } from '../../../store/slices/user';
import { NEAR_ZERO_HIDE_THRESHOLD } from '../../Overview/nearZero';

const Notification = () => {
  const userId = useSelector(selectUserId);

  const [editAreNotificationsEnabled] =
    useEditAreNotificationsEnabledMutation();
  const [areNotificationsEnabled, setAreNotificationsEnabled] = useState(true);

  const handleAreNotificationsEnabledChange = (value: boolean) => {
    editAreNotificationsEnabled({ userId, areNotificationsEnabled: value });
    setAreNotificationsEnabled(value);
  };

  return (
    <div>
      <p
        style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 20,
          marginBottom: 5,
        }}
      >
        Email Notifications
      </p>
      <Checkbox
        label={'I want to receive emails from transactions'}
        value={areNotificationsEnabled}
        handleValueChange={handleAreNotificationsEnabledChange}
        color={colors.orange}
      />
    </div>
  );
};

/**
 * The master switch for earning interest on idle balances.
 *
 * Unlike the notifications toggle above, this reads its REAL value from the server rather
 * than defaulting the control to a guess — it governs whether real funds are moved into a
 * third-party product, so showing the wrong state would be worse than showing none.
 *
 * It is only the FIRST of three gates. Each subscription opts in separately (Manage →
 * Savings), and a coin with no exchange earn product never parks regardless.
 */
const Savings = () => {
  const userId = useSelector(selectUserId);

  const { data, isLoading } = useGetUserSavingsEnabledQuery(userId, {
    skip: !userId,
  });
  const [editUserSavingsEnabled] = useEditUserSavingsEnabledMutation();
  const [savingsEnabled, setSavingsEnabled] = useState(false);

  // The real value arrives asynchronously; adopt it once it lands.
  useEffect(() => {
    if (data?.savingsEnabled !== undefined) {
      setSavingsEnabled(data.savingsEnabled);
    }
  }, [data?.savingsEnabled]);

  const handleSavingsEnabledChange = (value: boolean) => {
    setSavingsEnabled(value);
    editUserSavingsEnabled({ userId, savingsEnabled: value });
  };

  return (
    <div style={{ marginTop: 32 }}>
      <p
        style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 20,
          marginBottom: 5,
        }}
      >
        Savings
      </p>
      <Checkbox
        label={'Earn interest on idle balances'}
        value={savingsEnabled}
        handleValueChange={handleSavingsEnabledChange}
        color={colors.orange}
        isDisabled={isLoading}
      />
      <p
        style={{
          color: colors.gray,
          fontSize: 12,
          marginTop: 8,
          maxWidth: 620,
          lineHeight: 1.5,
        }}
      >
        Idle balances are parked in flexible savings and redeemed automatically
        the moment an order needs them. This is the master switch — each
        subscription still has to be enabled on its own, under Manage. Coins
        without a savings product on the exchange are never parked.
      </p>
      <p
        style={{
          color: colors.gray,
          fontSize: 12,
          marginTop: 6,
          maxWidth: 620,
          lineHeight: 1.5,
        }}
      >
        Turning this off stops new parking. Anything already parked stays
        visible and is redeemed as normal when an order needs it, so nothing
        gets stranded.
      </p>
    </div>
  );
};

/**
 * Hide retired bots that hold (almost) nothing.
 *
 * Presentational only. Every all-time figure is computed over EVERY subscription, retired ones
 * included — that is the point of counting them, since a retired bot's realised loss is real.
 * This only stops empty shells from cluttering the grid, charts and transaction list.
 *
 * Reads its real value from the server, like Savings above: a guessed default would tell the
 * user rows are being hidden when they are not, or the reverse.
 */
const HideNearZero = () => {
  const userId = useSelector(selectUserId);

  const { data, isLoading } = useGetHideNearZeroEnabledQuery(userId, {
    skip: !userId,
  });
  const [updateHideNearZeroEnabled] = useUpdateHideNearZeroEnabledMutation();
  const [hideNearZeroEnabled, setHideNearZeroEnabled] = useState(false);

  // The real value arrives asynchronously; adopt it once it lands.
  useEffect(() => {
    if (data?.hideNearZeroEnabled !== undefined) {
      setHideNearZeroEnabled(data.hideNearZeroEnabled);
    }
  }, [data?.hideNearZeroEnabled]);

  const handleHideNearZeroChange = (value: boolean) => {
    setHideNearZeroEnabled(value);
    updateHideNearZeroEnabled({ userId, hideNearZeroEnabled: value });
  };

  return (
    <div style={{ marginTop: 32 }}>
      <p
        style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 20,
          marginBottom: 5,
        }}
      >
        Hide near-zero bots
      </p>
      <Checkbox
        label={`Hide retired bots worth less than $${NEAR_ZERO_HIDE_THRESHOLD}`}
        value={hideNearZeroEnabled}
        handleValueChange={handleHideNearZeroChange}
        color={colors.orange}
        isDisabled={isLoading}
      />
      <p
        style={{
          color: colors.gray,
          fontSize: 12,
          marginTop: 8,
          maxWidth: 620,
          lineHeight: 1.5,
        }}
      >
        Removes retired bots that hold almost nothing from the grid, the charts
        and the transaction list.
      </p>
      <p
        style={{
          color: colors.gray,
          fontSize: 12,
          marginTop: 6,
          maxWidth: 620,
          lineHeight: 1.5,
        }}
      >
        This changes what you see, never what is counted. Retired bots always
        stay in the all-time profit — their realised result is real, and leaving
        it out would flatter the total. Active bots are never hidden, however
        little they hold.
      </p>
    </div>
  );
};

const AccountSettings = () => {
  return (
    <div style={{ padding: 46, paddingTop: 12 }}>
      <Notification />
      <Savings />
      <HideNearZero />
    </div>
  );
};

export default AccountSettings;
